import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Loader2, AlertTriangle, ShieldCheck, ShieldAlert, Trash2, Facebook } from 'lucide-react';
import {
    getMetaTemplatesForPage,
    deleteMetaTemplate,
    type MetaTemplateListItem,
} from '@/services/api/utility-templates';

interface PageMetaTemplatesCompareDialogProps {
    /** null = fechado. */
    pageId: string | null;
    pageName?: string;
    onOpenChange: (open: boolean) => void;
}

/**
 * Compara os message templates que a META conhece para uma página (via GET
 * direto na Graph API) com os que o NOSSO sistema conhece
 * (meta_utility_templates, por meta_template_id) — identifica órfãos
 * (criados fora deste sistema, ou de testes antigos) que podem estar
 * ocupando nome/limite na conta e causando o erro Meta código 10 ("This
 * page is restricted from creating templates").
 *
 * Exclusão é feita diretamente na conta REAL da Meta — IRREVERSÍVEL. Exige
 * digitar o nome exato do template para confirmar (barreira mais forte que
 * um simples "tem certeza?", dado o risco de dado real de cliente).
 */
export default function PageMetaTemplatesCompareDialog({
    pageId,
    pageName,
    onOpenChange,
}: PageMetaTemplatesCompareDialogProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [templateToDelete, setTemplateToDelete] = useState<MetaTemplateListItem | null>(null);

    const {
        data,
        isLoading,
        isError,
        error,
        refetch,
        isFetching,
    } = useQuery({
        queryKey: ['meta-templates-compare', pageId],
        queryFn: () => getMetaTemplatesForPage(pageId as string),
        enabled: pageId !== null,
        retry: false,
    });

    const { mutate: runDelete, isPending: deleting } = useMutation({
        // Envia sempre os dois: a Meta só aceita exclusão por template_name
        // (ver delete_meta_template no backend); meta_template_id sozinho só
        // resolve o nome se o registro local ainda tiver esse campo — o que
        // nem sempre é o caso (ex: registro resetado por um retry anterior),
        // então manda o nome já disponível na própria listagem, sem depender
        // do backend re-descobrir algo que o front já sabe.
        mutationFn: (tpl: MetaTemplateListItem) =>
            deleteMetaTemplate(pageId as string, { meta_template_id: tpl.id, template_name: tpl.name }),
        onSuccess: (_result, tpl) => {
            toast({ title: `Template "${tpl.name}" excluído da Meta.` });
            setTemplateToDelete(null);
            queryClient.invalidateQueries({ queryKey: ['meta-templates-compare', pageId] });
        },
        onError: (err: any) => {
            toast({
                title: 'Falha ao excluir na Meta',
                description: err?.response?.data?.detail ?? 'Tente novamente mais tarde.',
                variant: 'destructive',
            });
        },
    });

    const orphans = useMemo(
        () => (data?.templates ?? []).filter((t) => !t.known_to_system),
        [data]
    );
    const known = useMemo(
        () => (data?.templates ?? []).filter((t) => t.known_to_system),
        [data]
    );

    const handleClose = () => {
        setTemplateToDelete(null);
        onOpenChange(false);
    };

    return (
        <>
            <Dialog open={pageId !== null} onOpenChange={handleClose}>
                <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Comparar com a Meta{pageName ? ` — ${pageName}` : ''}</DialogTitle>
                    </DialogHeader>

                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800 flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-500" />
                        <p>
                            Esta tela consulta a conta REAL da Meta desta página. Templates marcados como
                            <strong> não criados por este sistema</strong> podem ser testes antigos, criações manuais,
                            ou de outra integração — eles podem estar ocupando nome/limite e causando o erro da Meta
                            "página restrita de criar templates" (código 10). Excluir um template aqui é uma ação
                            <strong> irreversível na conta da Meta do cliente</strong>.
                        </p>
                    </div>

                    {isLoading && (
                        <div className="flex justify-center py-12">
                            <Loader2 className="h-6 w-6 animate-spin text-cyan-500" />
                        </div>
                    )}

                    {isError && (
                        <div className="text-center py-10 px-4">
                            <AlertTriangle className="mx-auto h-8 w-8 text-red-400" />
                            <p className="mt-2 text-sm font-medium text-gray-900">Falha ao consultar a Meta</p>
                            <p className="mt-1 text-xs text-gray-500">
                                {(error as any)?.response?.data?.detail ?? 'Verifique o token da página e tente novamente.'}
                            </p>
                            <Button size="sm" variant="outline" className="mt-3" onClick={() => refetch()}>
                                Tentar novamente
                            </Button>
                        </div>
                    )}

                    {data && !isLoading && !isError && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-3 gap-2">
                                <SummaryTile label="Total na Meta" value={data.total} />
                                <SummaryTile label="Conhecidos" value={known.length} tone="cyan" />
                                <SummaryTile label="Órfãos" value={orphans.length} tone={orphans.length > 0 ? 'red' : 'gray'} />
                            </div>

                            <div className="flex items-center justify-between">
                                <p className="text-sm font-semibold text-gray-900">
                                    Templates ({data.templates.length})
                                </p>
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => refetch()}
                                    disabled={isFetching}
                                >
                                    {isFetching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Atualizar'}
                                </Button>
                            </div>

                            {data.templates.length === 0 ? (
                                <p className="text-sm text-gray-400 text-center py-6">
                                    Nenhum template encontrado na Meta para esta página.
                                </p>
                            ) : (
                                <div className="flex flex-col gap-1.5 max-h-[45vh] overflow-y-auto pr-1">
                                    {data.templates.map((tpl) => (
                                        <TemplateRow
                                            key={tpl.id}
                                            template={tpl}
                                            onRequestDelete={() => setTemplateToDelete(tpl)}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={handleClose}>
                            Fechar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <DeleteMetaTemplateConfirmDialog
                template={templateToDelete}
                isDeleting={deleting}
                onCancel={() => setTemplateToDelete(null)}
                onConfirm={(tpl) => runDelete(tpl)}
            />
        </>
    );
}

function SummaryTile({
    label,
    value,
    tone = 'gray',
}: {
    label: string;
    value: number;
    tone?: 'gray' | 'cyan' | 'red';
}) {
    const toneCls =
        tone === 'cyan'
            ? 'border-cyan-200 bg-cyan-50 text-cyan-700'
            : tone === 'red'
              ? 'border-red-200 bg-red-50 text-red-700'
              : 'border-gray-200 bg-white text-gray-700';
    return (
        <div className={`rounded-lg border px-3 py-2 text-center ${toneCls}`}>
            <p className="text-lg font-semibold leading-none">{value}</p>
            <p className="text-[10px] font-medium uppercase tracking-wider mt-1 opacity-80">{label}</p>
        </div>
    );
}

function TemplateRow({
    template,
    onRequestDelete,
}: {
    template: MetaTemplateListItem;
    onRequestDelete: () => void;
}) {
    return (
        <div
            className={`rounded-lg border px-3 py-2.5 flex items-center justify-between gap-3 ${
                template.known_to_system ? 'border-gray-100 bg-white' : 'border-red-100 bg-red-50/40'
            }`}
        >
            <div className="min-w-0 flex items-center gap-2.5">
                <div className="h-8 w-8 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                    <Facebook className="h-3.5 w-3.5 text-blue-600" />
                </div>
                <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{template.name}</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                        {template.status}
                        {template.language ? <span className="text-gray-300 mx-1">·</span> : null}
                        {template.language}
                        {template.category ? <span className="text-gray-300 mx-1">·</span> : null}
                        {template.category}
                    </p>
                    <p className="text-[10px] text-gray-400 mt-0.5">ID: {template.id}</p>
                </div>
            </div>
            <div className="shrink-0 flex items-center gap-2">
                {template.known_to_system ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border border-cyan-200 bg-cyan-50 text-cyan-700">
                        <ShieldCheck className="h-3 w-3" />
                        Conhecido pelo sistema
                    </span>
                ) : (
                    <>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border border-red-200 bg-red-50 text-red-700">
                            <ShieldAlert className="h-3 w-3" />
                            Não criado por este sistema
                        </span>
                        <Button
                            type="button"
                            size="sm"
                            variant="destructive"
                            className="h-7 px-2"
                            onClick={onRequestDelete}
                        >
                            <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
}

/**
 * Confirmação reforçada: exige digitar o nome EXATO do template antes de
 * habilitar o botão de exclusão. Padrão mais seguro que um simples "tem
 * certeza?" de duplo-clique acidental, dado que a ação é irreversível na
 * conta real de produção da Meta do cliente.
 */
function DeleteMetaTemplateConfirmDialog({
    template,
    isDeleting,
    onCancel,
    onConfirm,
}: {
    template: MetaTemplateListItem | null;
    isDeleting: boolean;
    onCancel: () => void;
    onConfirm: (template: MetaTemplateListItem) => void;
}) {
    const [confirmText, setConfirmText] = useState('');

    const handleOpenChange = (open: boolean) => {
        if (!open) {
            setConfirmText('');
            onCancel();
        }
    };

    const canConfirm = !!template && confirmText.trim() === template.name && !isDeleting;

    return (
        <Dialog open={template !== null} onOpenChange={handleOpenChange}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-red-50 border border-red-100 mb-1">
                        <Trash2 className="w-5 h-5 text-red-500 shrink-0" />
                        <DialogTitle className="text-sm font-semibold text-gray-800">
                            Excluir template da conta da Meta
                        </DialogTitle>
                    </div>
                </DialogHeader>

                {template && (
                    <div className="space-y-3 px-1">
                        <p className="text-sm text-gray-600">
                            Você está prestes a excluir <strong>permanentemente</strong> o template{' '}
                            <span className="font-mono text-gray-900">{template.name}</span> diretamente da conta da
                            Meta desta página. Esta ação <strong>não pode ser desfeita</strong> e afeta dados reais de
                            produção do cliente.
                        </p>
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-gray-600">
                                Digite <span className="font-mono font-semibold">{template.name}</span> para confirmar:
                            </label>
                            <Input
                                value={confirmText}
                                onChange={(e) => setConfirmText(e.target.value)}
                                placeholder={template.name}
                                autoFocus
                            />
                        </div>
                    </div>
                )}

                <DialogFooter className="gap-2 sm:gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleOpenChange(false)} disabled={isDeleting}>
                        Cancelar
                    </Button>
                    <Button
                        size="sm"
                        className="bg-red-600 hover:bg-red-700 text-white"
                        onClick={() => template && onConfirm(template)}
                        disabled={!canConfirm}
                    >
                        {isDeleting && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                        Excluir definitivamente
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
